import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarDays, Clock, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTABanner from "@/components/CTABanner";
import { BLOG_POSTS } from "@/lib/blog-posts";

type BlogPost = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  category: string;
  featured_image_url: string | null;
  created_at: string;
};

// Static posts (full content lives in src/lib/blog-posts.ts)
const staticPosts: BlogPost[] = BLOG_POSTS.map((p, i) => ({
  id: `static-${i}`,
  slug: p.slug,
  title: p.title,
  excerpt: p.excerpt,
  category: p.category,
  featured_image_url: p.image,
  created_at: p.date,
}));

const categories = [
  { slug: "all", label: "All" },
  { slug: "invisalign", label: "Invisalign" },
  { slug: "root-canal", label: "Root Canal" },
  { slug: "implants", label: "Implants" },
  { slug: "cosmetic-dentistry", label: "Cosmetic" },
  { slug: "kids-dental-care", label: "Kids Dental" },
  { slug: "general-dental-care", label: "General" },
];

const BlogHub = () => {
  const { category } = useParams<{ category?: string }>();
  const activeCategory = category || "all";
  const [posts, setPosts] = useState<BlogPost[]>(staticPosts);

  useEffect(() => {
    const fetchPosts = async () => {
      const { data } = await supabase.from("blogs").select("*").eq("is_published", true).order("created_at", { ascending: false });
      if (data && data.length > 0) setPosts(data);
    };
    fetchPosts();
  }, []);

  const filtered = activeCategory === "all" ? posts : posts.filter(p => p.category === activeCategory);
  const breadcrumbs = category
    ? [{ label: "Blog", path: "/blogs" }, { label: categories.find(c => c.slug === category)?.label || category }]
    : [{ label: "Blog" }];

  return (
    <div>
      <section className="bg-secondary py-12 md:py-16">
        <div className="container">
          <Breadcrumbs items={breadcrumbs} />
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-secondary-foreground mb-3">
            Dental Health Tips & Guides
          </h1>
          <p className="text-secondary-foreground/80">by Dr. Khushboo Mutha</p>
        </div>
      </section>

      <section className="py-16">
        <div className="container">
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Button
                key={cat.slug}
                variant={activeCategory === cat.slug ? "default" : "outline"}
                size="sm"
                asChild
              >
                <Link to={cat.slug === "all" ? "/blogs" : `/blogs/category/${cat.slug}`}>{cat.label}</Link>
              </Button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <p className="text-muted-foreground text-center py-12">No posts in this category yet. Check back soon!</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((post, i) => (
                <Link to={`/blogs/${post.slug}`} key={post.id}>
                  <Card className="h-full overflow-hidden hover:shadow-soft-lg transition group">
                    <CardContent className="p-0">
                      {post.featured_image_url ? (
                        <img src={post.featured_image_url} alt={post.title} className="w-full h-56 object-contain bg-muted" loading="lazy" />
                      ) : (
                        <div className="bg-muted h-56" />
                      )}
                      <div className="p-5">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                          <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {new Date(post.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                        </div>
                        <h2 className="font-heading font-semibold mb-2 group-hover:text-primary transition">{post.title}</h2>
                        <p className="text-sm text-muted-foreground mb-3">{post.excerpt}</p>
                        <span className="text-sm font-medium text-primary inline-flex items-center gap-1 group-hover:underline">
                          Read More <ArrowRight className="h-3 w-3" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTABanner title="Have Dental Questions?" subtitle="Chat with Dr. Mutha on WhatsApp for free advice" />
    </div>
  );
};

export default BlogHub;
